import React from 'react';
import { useSelector } from 'react-redux';
import {
  IonIcon,
  IonLabel,
  IonSelect,
  IonSelectOption,
  useIonPicker
} from '@ionic/react';
import { RootState } from 'core/store';
import ArrowIcon from 'assets/icons/arrow-down.svg';
import 'components/hk-select/HKSelect.scss';

export interface HKSelectOption {
  value: string | number;
  label: string;
}

export interface HKSelectProps {
  /** Omit to render without a field label. */
  label?: string;
  value: string | number | null;
  placeholder?: string;
  disabled?: boolean;
  options: HKSelectOption[];
  /** Appended alongside the actual select/trigger element's class — e.g. to override sizing/color per usage. */
  className?: string;
  /** Appended alongside the label's class — e.g. to override its color per usage. */
  labelClassName?: string;
  /** Appended alongside the root wrapper's class — e.g. to add a border/spacing box around the whole field. */
  wrapperClassName?: string;
  onChange: (value: string | number | null) => void;
}

/**
 * Responsive select: a popover `IonSelect` on desktop, an `IonPicker` on
 * mobile — replacing every ad-hoc `IonSelect`/`useIonPicker()` call site in
 * the app with one shared component. See RHC10-7419.
 */
const HKSelect = ({
  label,
  value,
  placeholder,
  disabled,
  options,
  className,
  labelClassName,
  wrapperClassName,
  onChange
}: HKSelectProps): JSX.Element => {
  const { isDesktopWidth } = useSelector((state: RootState) => state.platform);
  const [present] = useIonPicker();

  const selectedLabel = options.find((option) => option.value === value)?.label;

  const openPicker = () => {
    if (disabled) return;
    present({
      cssClass: 'hk-picker',
      mode: 'ios',
      columns: [
        {
          name: 'value',
          selectedIndex: options.findIndex((option) => option.value === value),
          options: options.map((option) => ({
            text: option.label,
            value: option.value
          }))
        }
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'Confirm',
          handler: (result) => onChange(result.value.value)
        }
      ]
    });
  };

  return (
    <div
      className={`hk-select${wrapperClassName ? ` ${wrapperClassName}` : ''}`}
    >
      {label && (
        <IonLabel
          className={`hk-select-label${
            labelClassName ? ` ${labelClassName}` : ''
          }`}
        >
          {label}
        </IonLabel>
      )}
      {isDesktopWidth ? (
        <IonSelect
          className={`hk-select-input${className ? ` ${className}` : ''}`}
          fill="outline"
          interface="popover"
          interfaceOptions={{
            cssClass: 'hk-select-popover',
            showBackdrop: true
          }}
          toggleIcon={ArrowIcon}
          aria-label={label || placeholder || 'Select...'}
          placeholder={placeholder || 'Select...'}
          disabled={disabled}
          value={value}
          onIonChange={(e) => onChange(e.detail.value ?? null)}
        >
          {options.map((option) => (
            <IonSelectOption key={option.value} value={option.value}>
              {option.label}
            </IonSelectOption>
          ))}
        </IonSelect>
      ) : (
        // IonButton exception: this trigger must render its own custom layout
        // (label/placeholder text + chevron icon side by side, no button
        // chrome) to visually match the desktop IonSelect field, which
        // IonButton's fixed slot structure can't reproduce — hence the plain
        // div with role="button" instead.
        <div
          className={`hk-select-input hk-select-mobile-trigger${
            className ? ` ${className}` : ''
          }${disabled ? ' hk-select-mobile-trigger-disabled' : ''}`}
          role="button"
          tabIndex={disabled ? undefined : 0}
          aria-disabled={disabled}
          aria-label={label || placeholder || 'Select...'}
          onClick={openPicker}
          onKeyDown={(event) => {
            if (disabled) return;
            if (event.key === 'Enter' || event.key === ' ') {
              // Space would otherwise scroll the page instead of opening.
              event.preventDefault();
              openPicker();
            }
          }}
        >
          <span
            className={
              selectedLabel
                ? 'hk-select-mobile-trigger-text'
                : 'hk-select-mobile-trigger-placeholder'
            }
          >
            {selectedLabel || placeholder || 'Select...'}
          </span>
          <IonIcon icon={ArrowIcon} />
        </div>
      )}
    </div>
  );
};

export default HKSelect;
